/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import axios from 'axios';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { Info, User, CheckSquare, Calendar, UserCheck } from 'react-feather';
import Blowfish from 'es6-sladex-blowfish';
import { components } from 'react-select';
import i18next from 'i18next';
import heic2any from 'heic2any';

import history from './history';
import { getLocalStorage } from './storageUtil';
import {
  DOCUMENT_PREVIEW_VIEW,
  DOCUMENT_SIDEBAR_PREVIEW_HEIGHT,
  DOCUMENT_SIDEBAR_PREVIEW_TARGET_ID,
  withPdfDocumentTitle,
} from './fileUploadUtils';
import {
  ADMIN_JWT_TOKEN,
  ADMIN_SELECTED_LANGUAGE,
  API_URL,
  BUSINESS,
  CLIENT_JWT_TOKEN,
  INDIVIDUAL,
  ORDINAL_DATE_FORMAT,
  SYSTEM_JWT_TOKEN,
  TIME_12H_FORMAT,
} from '../constants/appConfig';
import { adminAuth, adminFirebase } from '../FirebaseAdminConfig';
import Toast from '../components/shared/Toast';
import { loggerFn } from './loggerUtil';
export const getNameInitial = (name) => {
  if (!name) {
    return '-';
  }
  return name?.toUpperCase()?.split(' ')?.[0]?.split('')?.[0];
};

export const getLastNameInitial = (name) => {
  return name?.toUpperCase()?.split(' ')?.[1]?.split('')?.[0];
};

export const limitNameWithLength = (word, length) => {
  let screenSize = window?.innerWidth;
  let displayWord = '';
  if (!word) return '';
  if (word instanceof Object && isEmpty(word)) return '';
  if (word && word.length <= length) return word;

  if (length >= 15) {
    switch (true) {
      case screenSize >= 1700:
        displayWord = word.substring(0, length);
        return displayWord + '...';

      case screenSize >= 1200:
        displayWord = word.substring(0, length - 5);
        return displayWord + '...';

      case screenSize >= 1023:
        displayWord = word.substring(0, length - 8);
        return displayWord + '...';

      case screenSize >= 767:
        displayWord = word.substring(0, length - 10);
        return displayWord + '...';

      case screenSize >= 480:
        displayWord = word.substring(0, length - 12);
        return displayWord + '...';

      case screenSize >= 440:
        displayWord = word.substring(0, length - 10);
        return displayWord + '...';
    }
  } else {
    displayWord = word.substring(0, length);
    return displayWord + '...';
  }
};

export const hasTouched = (isFieldsTouched) => {
  return isFieldsTouched();
};

export const hasErrors = (fieldsError) => {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
};

export const removeWhiteSpace = (value) => {
  var name = value.trim().split(/ +/).join(' ');
  return name;
};

export const download = (blob, name, ext) => {
  let link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${name}.${ext}`;
  link.click();
};

export const downloadDocument = (
  { id, name, type, year, docId, app, extension, documentType, folderId },
  setDownloadLoading = () => {}
) => {
  let blob;
  let api;
  let documentName = name.split('.');

  if (app === 'admin') {
    switch (documentType) {
      case 'message-attachment':
        api = API_URL + `/atom-manager/v1/messages/new/download/attachment/${id}`;
        break;

      case 'document':
        api =
          API_URL + `/atom-manager/v1/clients/new/download/document/${type}/${id}/${year}/${docId}`;
        break;

      case 'folder':
        api =
          API_URL +
          `/atom-manager/v1/clients/new/download/folder/${type}/${id}/${folderId}/${docId}`;
        break;

      case 'criteria-acceptance':
        api = API_URL + `/atom-manager/v1/clients/download/sample`;
        break;

      case 'nexio':
        api =
          API_URL + `/atom-manager/v1/company/integration/sample/payment-gateway/${documentType}`;
        break;

      case 'google-calendar':
        api = API_URL + `/atom-manager/v1/company/integration/sample/google-calender`;
        break;
    }
  } else if (app === 'system') {
    switch (documentType) {
      case 'nexio':
        api = API_URL + `/atom-admin/v1/company/integration/sample/payment-gateway/${documentType}`;
        break;

      case 'google-calendar':
        api = API_URL + `/atom-admin/v1/company/integration/sample/google-calender`;
        break;
    }
  } else if (app === 'system-sign') {
    switch (documentType) {
      case 'nexio':
        api =
          API_URL + `/atom-admin/v1/register/integration/sample/payment-gateway/${documentType}`;
        break;
    }
  } else {
    switch (documentType) {
      case 'message-attachment':
        api = API_URL + `/atom-client/v1/messages/new/download/attachment/${id}`;
        break;

      case 'document':
        api =
          API_URL + `/atom-client/v1/infos/new/download/document/${type}/${id}/${year}/${docId}`;
        break;

      case 'folder':
        api =
          API_URL + `/atom-client/v1/infos/new/download/folder/${type}/${id}/${folderId}/${docId}`;
        break;
    }
  }
  const commonHeader = {
    'X-XSRF-TOKEN':
      app === 'system'
        ? getLocalStorage(SYSTEM_JWT_TOKEN)
        : app === 'client' && getLocalStorage(CLIENT_JWT_TOKEN),
    'X-TIME-ZONE': getCurrentTimeZone(),
  };

  const adminHeader = {
    'client-device': 'web',
    'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
    'X-TIME-ZONE': getCurrentTimeZone(),
    lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
  };
  setDownloadLoading(true);
  axios
    .create({
      headers: app === 'admin' ? { ...adminHeader } : { ...commonHeader },
      responseType: 'arraybuffer',
    })
    .get(api)
    .then(async (response) => {
      let fileUrl = null;

      try {
        const decodedString = new TextDecoder('utf-8').decode(new Uint8Array(response.data));
        const jsonData = JSON.parse(decodedString);
        fileUrl =
          jsonData?.data?.fileUrl || jsonData?.data?.url || jsonData?.fileUrl || jsonData?.url;
      } catch (err) {
        // Not a JSON response or failed to parse. Fall back to direct binary.
      }

      if (fileUrl) {
        try {
          const fileResponse = await axios.get(fileUrl, { responseType: 'arraybuffer' });
          if (fileResponse.data && fileResponse.data.byteLength > 0) {
            setDownloadLoading(false);
            blob = new Blob([fileResponse.data], {
              type: extension ? `application/${extension}` : `application/pdf`,
            });
            download(blob, documentName[0], extension ? `${extension}` : `pdf`);
          }
          return;
        } catch (err) {
          console.error('Error downloading from fileUrl:', err);
        }
      }

      if (response.data && response.data.byteLength > 0) {
        setDownloadLoading(false);
        blob = new Blob([response.data], {
          type: extension ? `application/${extension}` : `application/pdf`,
        });
        download(blob, documentName[0], extension ? `${extension}` : `pdf`);
      }
    })
    .catch((e) => {
      Toast(i18next.t('msg_doc_download_fail'), 'ERROR');
      setDownloadLoading(false);
    });
};

export const iconForNotification = (redirectLink) => {
  const redirectType = redirectLink?.split('/')[0];
  switch (redirectType) {
    case 'APPOINTMENT':
      return Calendar;
    case 'TAX_REVIEW':
      return UserCheck;
    case 'CLIENT_TAX_REPRESENTATIVE':
      return User;
    case 'TASK':
      return CheckSquare;
    default:
      return Info;
  }
};

export const redirectNotification = (type = '', redirectLink, message = {}) => {
  const redirectType = redirectLink?.split('/')[0];
  const redirectId = redirectLink?.split('/')[1];
  const redirectSenderType = redirectLink?.split('/')[2];

  if (type.toLowerCase() === 'admin') {
    switch (redirectType) {
      case 'APPOINTMENT':
        return history.push(`/admin/appointments/${redirectId}/detail`);

      case 'TAX_REVIEW':
        return history.push(`/admin/portalrelease/${redirectId}/INDIVIDUAL/detail/list-to-review`);

      case 'CLIENT_TAX_REPRESENTATIVE':
        return history.push('/admin/clients');

      case 'TASK':
        return history.push(`/admin/tasks/redirect/${redirectId}`);

      case 'MESSAGE':
        return history.push(
          `/admin/messages/${redirectId}/detail/${redirectSenderType.toLowerCase()}`
        );

      case 'DOCUMENT-INDIVIDUAL':
        return history?.push(
          `/admin/clients/${redirectId?.split('-')[0]}/individual/detail?tab=documents`
        );

      case 'DOCUMENT-BUSINESS':
        return history?.push(
          `/admin/clients/${redirectId?.split('-')[0]}/business/detail?tab=documents`
        );

      case 'CHAT':
        return history?.push(`/admin/chat`);

      default:
        return history.push('/admin/notifications');
    }
  } else {
    switch (redirectType) {
      case 'APPOINTMENT':
        return history.push(`/client/appointment/${redirectId}`);

      case 'DOCUMENT':
        return history.push(`/client/documents/${redirectId}`);

      case 'CLIENT_TAX_REPRESENTATIVE':
        return history.push(`/client/documents`);

      case 'MESSAGE':
        return history.push(`/client/messages/${redirectId}/detail`);

      default:
        return history.push('/client/dashboard');
    }
  }
};

export const getSortingOrder = (sorterOrdered) => {
  let orderType;
  if (sorterOrdered) {
    if (sorterOrdered === 'descend') {
      orderType = 'DESCENDING';
    } else {
      orderType = 'ASCENDING';
    }
  }
  return orderType;
};

export const disabledPastDate = (current) => {
  return current && current < moment().startOf('day');
};

export const disabledFutureDate = (current) => {
  return current && current > moment().endOf('day');
};

export const getStatusColor = (status) => {
  switch (JSON.stringify(status)) {
    case 'true':
      return 'success';
    case 'false':
      return 'warning';
    default:
      return 'primary';
  }
};

export const getPriorityColor = (priority) => {
  switch (priority) {
    case 'HIGH':
      return 'danger';
    case 'MEDIUM':
      return 'warning';
    default:
      return 'success';
  }
};

export const getTaskStatusColor = (status) => {
  switch (status) {
    case 'OPEN':
      return 'primary';
    case 'COMPLETE':
      return 'success';
    case 'SKIP':
      return 'danger';
    case 'PARK':
      return 'warning';
    case 'PARKED':
      return 'warning';
    case 'CLOSE':
      return 'danger';
    default:
      return 'success';
  }
};

/* Statuses reach us both as api codes (`in_progress`, `on-hold`) and as agent labels
 * (`In progress`), so they are normalised before the colour is picked. */
export const getSupportStatusColor = (status) => {
  switch (status?.toLowerCase()?.replace(/[-_]/g, ' ')) {
    case 'new':
    case 'in progress':
    case 'inprogress':
      return '#7b8cf9';
    case 'open':
      return '#43d39e';
    case 'pending':
      return '#adb5bd';
    case 'hold':
    case 'on hold':
      return '#43e39e';
    case 'close':
    case 'closed':
      return '#ff7d8f';
    case 'solved':
      return '#43d39e';
    default:
      return '#43d39e';
  }
};

export const getEfileStatusColor = (status) => {
  switch (status) {
    case 'Prep Efile':
      return 'primary';
    case 'E-filed':
      return 'info';
    case 'Parked':
      return 'warning';
    case 'Rejected':
      return 'danger';
    case 'Mailed':
      return 'secondary';
    case 'Complete':
      return 'success';
    default:
      return 'success';
  }
};

export const getInvoiceStatusColor = (status) => {
  switch (status) {
    case 'DRAFT':
      return 'dark';
    case 'UNPAID':
      return 'primary';
    case 'VOID':
      return 'danger';
    case 'REVIEW':
      return 'warning';
    case 'REFUND':
      return 'info';
    default:
      return 'success';
  }
};

export const isEmpty = (obj) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

export const getAge = (date) => {
  let todayMoment = moment();
  let dateMoment = moment(date, 'YYYY-MM-DD');
  return todayMoment.diff(dateMoment, 'years');
};

export const camelCase = (words) => {
  if (words) {
    let tempWords = words.split(' ');
    tempWords = tempWords.map((word) =>
      word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : undefined
    );
    return tempWords.join(' ');
  } else {
    return words;
  }
};

export const trimName = (name) => {
  if (name.length < 30) {
    return name;
  } else {
    return name.slice(0, 30) + '...';
  }
};

export const formatDateAndTime = (itemDate) => {
  const today = moment().toDate();
  const momentItemDate = moment(itemDate);

  if (
    today.getFullYear() === momentItemDate.year() &&
    today.getMonth() === momentItemDate.month() &&
    today.getDate() === momentItemDate.date()
  ) {
    return `${momentItemDate.format('hh:mm A')}`;
  } else {
    if (today.getFullYear() === momentItemDate.year()) {
      return momentItemDate.format('MMM DD');
    } else {
      return momentItemDate.format('DD-MMM-YYYY');
    }
  }
};

export const checkAppointmentDate = (itemDate) => {
  return moment(itemDate).format('MMM DD, YYYY');
};

export const messageSubjectRefactoring = (subject) => {
  if (subject.match(/FWR : /) && subject.match(/FWR : /).length > 0) {
    return subject.slice(6);
  }

  if (subject.match(/RE : /) && subject.match(/RE : /).length > 0) {
    return subject.slice(5);
  }

  return subject;
};

export const handleEINShow = (value) => {
  let returnValue = value;
  if (value) {
    let x = value.match(/(\d{2}|[X]{2})(\d{3}|[X]{3})(\d{4})/);
    if (x === null) {
      returnValue = value;
    } else {
      returnValue = x[1] + '-' + x[2] + x[3];
    }
  }
  return returnValue;
};

export const handleSSNShow = (value) => {
  let returnValue = value;
  if (value) {
    let x = value.match(/(\d{3}|[X]{3})(\d{2}|[X]{2})(\d{4})/);
    if (x === null) {
      returnValue = value;
    } else {
      returnValue = x[1] + '-' + x[2] + '-' + x[3];
    }
  }
  return returnValue;
};

export const handlePhoneNumberShow = (value) => {
  if (!value) return undefined;

  value = value.replace(/\D/g, '');
  let returnValue = value;
  if (value) {
    let x = value.replace(/\D/g, '').match(/(\d{3})(\d{3})(\d{4})/);
    if (x === null) {
      returnValue = value;
    } else {
      returnValue = '(' + x[1] + ')-' + x[2] + '-' + x[3];
    }
  }

  return returnValue;
};

export const convertFetchedTimeInAMPMFormat = (time, format = 'HH:mm') => {
  return moment(time, format).format('h:mm A');
};

export const convertFetchedTimeInDate = (time) => {
  return moment(time, 'HH:mm');
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

export const exactMatchByKey = (matchVal, myArray, matchKey = 'key') => {
  if (myArray) {
    for (let i = 0; i < myArray.length; i++) {
      if (myArray?.[i]?.[matchKey] === matchVal) {
        return myArray[i];
      }
    }
  }
  return undefined;
};

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const minDateToday = () => {
  return moment().format('YYYY-MM-DD');
};

export const minCurrentTime = () => {
  return moment().format('HH:mm');
};

export const addDaysToDate = (days, date) => {
  return moment(date).add(days, 'days').format('YYYY-MM-DD');
};

export const addTime = (duration, time) => {
  return moment(time, 'HH:mm').add(duration, 'hours').format('HH:mm');
};

export const formatDateToStandard = (date) => {
  return moment(date).format('YYYY-MM-DD');
};

export const standardDisplayDateFormat = (date = '') => {
  return moment(date).format('DD-MMM-YYYY');
};

export const standardDisplayDateFormatWithTime = (date = '') => {
  return moment(date).format('ddd, DD-MMM-YYYY, H:mm');
};

export const standardDisplayDateFormatWithTime12Hformat = (date = '') => {
  return (
    <>
      {moment(date).format('ddd, DD-MMM-YYYY,')}
      <br />
      {moment(date).format('h:mm A')}
    </>
  );
};

/* Ordinal day without the weekday, e.g. "17th Aug, 2026" over "7:12 AM". */
export const ordinalDisplayDateFormatWithTime12Hformat = (date = '') => {
  return (
    <>
      {moment(date).format(ORDINAL_DATE_FORMAT)}
      <br />
      {moment(date).format(TIME_12H_FORMAT)}
    </>
  );
};

/* Ordinal day only, e.g. "17th Aug, 2026". */
export const ordinalDisplayDateFormat = (date = '') => {
  return moment(date).format(ORDINAL_DATE_FORMAT);
};

export const formatDateAndTimeForNote = () => {
  return moment().format('DD-MMM-YYYY HH:mm');
};

export const getFirstName = (name) => {
  return name?.split(' ')[0];
};
export const getLastName = (name) => {
  return name?.split(' ')[name?.split(' ')?.length - 1];
};

export const urlToList = (url) => {
  if (url) {
    const urlList = url.split('/').filter((i) => i);
    return urlList;
  }
};

export const getTaskDuration = (endDate) => {
  let duration;

  let differenceInDays = moment(endDate).diff(moment(), 'days') + 1;
  let differenceInMonths = moment(endDate).diff(moment(), 'months') + 1;

  if (differenceInDays > 0) {
    if (differenceInDays > 31) {
      duration = { days: differenceInMonths, type: 'months' };
    } else {
      duration = { days: differenceInDays, type: 'days' };
    }
  } else {
    duration = { date: moment(endDate).format('LL'), type: 'date' };
  }
  return duration;
};

export const getTimeOffDays = (startDate, endDate) => {
  let startingDate = moment(startDate).toDate();
  let endingDate = moment(endDate).toDate();

  let days =
    moment(endingDate?.getTime() - startingDate?.getTime()).toDate() / (1000 * 60 * 60 * 24);

  return days;
};

export const fetchSecureImg = async (
  id,
  type,
  documentItem,
  isFolderDocument = false,
  selectedFolder,
  selectedYear,
  setLoading,
  view
) => {
  const isSidebarView = view === DOCUMENT_PREVIEW_VIEW.SIDEBAR;
  /* The sidebar shares the rail with the notes tabs and the two metadata rows, so the preview
   * takes the viewport height left over from those. Keep in sync with the `--preview-height`
   * on `.document_detail_body__preview`. */
  const previewHeight = isSidebarView ? DOCUMENT_SIDEBAR_PREVIEW_HEIGHT : '94vh';

  let apiUrl = isFolderDocument
    ? `/atom-manager/v1/clients/new/download/folder/${type}/${id}/${selectedFolder.id}/${documentItem.id}`
    : `/atom-manager/v1/clients/new/download/document/${type}/${id}/${selectedYear.year}/${documentItem.id}`;

  const getBlobWithDataType = async (data) => {
    let blob;
    let docUrl;
    let imgDoc;
    const extension = documentItem?.extension?.toLowerCase();

    if (extension === 'heic' || extension === 'heif') {
      try {
        blob = new Blob([data], { type: extension === 'heic' ? 'image/heic' : 'image/heif' });
        const convertedBlob = await heic2any({
          blob: blob,
          toType: 'image/jpeg',
          quality: 0.8,
        });
        docUrl = window.URL.createObjectURL(convertedBlob);
        imgDoc = document.createElement('img');
        imgDoc.style = `max-width:100%;max-height:${previewHeight};width:auto;height:auto;object-fit:contain;display:block;margin:auto;`;
      } catch (error) {
        console.error('Error converting HEIC/HEIF to JPEG:', error);
        // Fallback to original blob if conversion fails
        blob = new Blob([data], { type: extension === 'heic' ? 'image/heic' : 'image/heif' });
        docUrl = window.URL.createObjectURL(blob);
        imgDoc = document.createElement('img');
        imgDoc.style = `max-width:100%;max-height:${previewHeight};width:auto;height:auto;object-fit:contain;display:block;margin:auto;`;
      }
    } else if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') {
      const mimeType = extension === 'jpg' ? 'image/jpg' : `image/${extension}`;
      blob = new Blob([data], { type: mimeType });
      docUrl = window.URL.createObjectURL(blob);
      imgDoc = document.createElement('object');
      imgDoc.type = mimeType;
      imgDoc.style = `width:100%;height:${previewHeight};`;
    } else {
      // Default to PDF
      blob = new Blob([await withPdfDocumentTitle(data, documentItem?.name)], {
        type: 'application/pdf',
      });
      docUrl = window.URL.createObjectURL(blob);
      imgDoc = document.createElement('object');
      imgDoc.style = `width:100%;height:${previewHeight};`;
      imgDoc.type = 'application/pdf';
    }

    imgDoc.src = `${docUrl}#view=Fit`;
    imgDoc.data = `${docUrl}#view=Fit`;
    imgDoc.id = 'document-id-target';
    return [imgDoc, blob];
  };

  return axios
    .create({
      baseURL: `${API_URL}`,
      headers: {
        'X-XSRF-TOKEN': getLocalStorage(ADMIN_JWT_TOKEN),
        'X-TIME-ZONE': getCurrentTimeZone(),
        'client-device': 'web',
        lang: getLocalStorage(ADMIN_SELECTED_LANGUAGE),
      },
    })
    .get(apiUrl)
    .then(async (response) => {
      return axios
        .create({
          responseType: 'arraybuffer',
        })
        .get(response.data.data.fileUrl)
        .then(async (response2) => {
          if (response2.data && response2.data.byteLength > 0) {
            setLoading(false);

            // Display file at #document-target
            let blobWithDataType = await getBlobWithDataType(response2.data);
            var imgDoc = blobWithDataType[0];

            const targetSelector = isSidebarView
              ? `#${DOCUMENT_SIDEBAR_PREVIEW_TARGET_ID}`
              : '#document-target';

            if (document.querySelector(targetSelector)) {
              document.querySelector(targetSelector).innerHTML = '';
              document.querySelector(targetSelector).appendChild(imgDoc);
            }
          }
        });
    });
};

export const adminAppHandleFirebaseProfileLoginOrCreation = (
  accessCode,
  firebaseEmail,
  firebasePassword
) => {
  if (adminFirebase.firebase_.apps.length && accessCode !== null) {
    adminAuth
      .signInWithEmailAndPassword(`${firebaseEmail}`, `${firebasePassword}`)
      .then((response) => {
        if (response?.operationType === 'signIn' && response?.user?.uid) {
          console.info('FIREBASE-SIGNIN, ---> User logged-in for firebase');
        } else {
          //do smth
        }
      })
      .catch((error) => {
        if (error.code === 'auth/user-not-found') {
          adminAuth
            .createUserWithEmailAndPassword(`${firebaseEmail}`, `${firebasePassword}`)
            .then((response) => {
              if (response?.user?.uid) {
                console.info('FIREBASE-SIGNUP, ---> User Created for firebase');
              } else {
                //do smth
              }
            })
            .catch((error) => {
              console.error('FIREBASE-SIGNUP-ERROR', error.message);
              loggerFn(error);
            });
        }
      });
  }
};

export const isBrowserSafari = () => {
  const isSafari =
    /constructor/i.test(window.HTMLElement) ||
    (function (p) {
      return p.toString() === '[object SafariRemoteNotification]';
    })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

  return isSafari;
};

/*
 * Return the return links for Exception folder.
 */
export const getReturnLinksForExceptions = () => {
  let checker = window.location.pathname.split('/')?.[1]?.toLowerCase();
  let returnLink = '/';
  switch (checker) {
    case 'admin':
      returnLink = '/admin/dashboard';
      break;
    case 'client':
      returnLink = '/client/dashboard';
      break;
    default:
      returnLink = '/';
      break;
  }

  return returnLink;
};

/*
 * Return the name from given array of data.
 */
export const findName = (givenArray, code) => {
  if (!givenArray || isEmpty(givenArray)) {
    return '';
  }

  let result = givenArray.find((item) => item.code === code || item.id === code);

  if (!result) {
    return '';
  } else {
    return result.title || result.name;
  }
};

export const fetchFavicon = (companyData) => {
  if (companyData && companyData.title) {
    document.title = companyData.title;
    let link;
    if (companyData.accessCode && companyData.favicon) {
      link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href =
        API_URL +
        `/atom-admin/v1/company/public/${companyData.accessCode}/image/${companyData.favicon}`;
    } else {
      link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = API_URL + '/Pacesetterlogo_icon.png';
    }
  } else {
    document.title = 'Pacesetter';
  }
};

export const handleBlowfishEncryption = (dataToEncrypt, token) => {
  let clearText = JSON.stringify(dataToEncrypt);
  let utf8Encode = new TextEncoder();
  let secretKey = utf8Encode.encode(token);
  let encryptedData = Blowfish.encrypt(clearText, secretKey);
  return encryptedData;
};

export const handleBlowfishDecryption = (dataToDecrypt, token) => {
  let utf8Encode = new TextEncoder();
  let secretKey = utf8Encode.encode(token);
  let jsonDecryptedData = Blowfish.decrypt(dataToDecrypt, secretKey);
  let decryptedData = JSON.parse(jsonDecryptedData);
  return decryptedData;
};

export const quickbookBlowfishEncryption = (dataToEncrypt, token) => {
  let utf8Encode = new TextEncoder();
  let secretKey = utf8Encode.encode(token);
  let encryptedData = Blowfish.encrypt(dataToEncrypt, secretKey);
  return encryptedData;
};

/**
 * Copies texts or link...
 * @param {string} text
 */
export const copyToClipboard = (text) => {
  var textField = document.createElement('textarea');
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
};

/**
 * This function is used to get the user name by the given user email id
 * @param {array} usersArray
 * @param {string} userCode
 * @returns
 */
export const userNameMapper = (usersArray, userCode) => {
  return usersArray?.find((user) => user?.data.toLowerCase() === userCode.toLowerCase())?.title;
};

/**
 * This function is used to get the sum of ascii value of
 * each characted of the given string
 * @param {string} name
 * @returns
 */
export const getASCIISumValueOfName = (name = '') => {
  let inputNameCharArray = name.split('');
  let sumOfAsciiValue = 0;
  inputNameCharArray.forEach((userNameChar) => {
    if (userNameChar != ' ') {
      let asciiValue = userNameChar.charCodeAt(0);
      sumOfAsciiValue = sumOfAsciiValue + asciiValue;
    }
    return sumOfAsciiValue;
  });
  return sumOfAsciiValue;
};

/**
 * Get color by ascii value sum
 * @param {string} username
 * @returns
 */
export const getRandomTagColor = (username) => {
  let asciiValueSum = getASCIISumValueOfName(username);
  let remainder = asciiValueSum % 10;
  switch (remainder) {
    case 0:
      return '#ff6666';

    case 1:
      return '#96c496';

    case 2:
      return '#cccc7a';

    case 3:
      return '#98909e';

    case 4:
      return '#cf9571';

    case 5:
      return '#dbacdd';

    case 6:
      return '#a1aece';

    case 7:
      return '#b2e59c';

    case 8:
      return '#f5bfd2';

    case 9:
      return '#bcc5b4';

    default:
      return '#e67e96';
  }
};

export const getCurrentTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function getCurrentUrl(location) {
  return location.pathname.split(/[?#]/)[0];
}

export function checkIsActive(location, url) {
  const current = getCurrentUrl(location);
  if (!current || !url) {
    return false;
  }

  if (current === url) {
    return true;
  }

  if (current.indexOf(url) > -1) {
    return true;
  }

  return false;
}

export const invoiceAmountBillFormat = (amount) => {
  if (!amount) {
    return 0;
  }

  return '$ ' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const invoiceAmountFormat = (amount) => {
  if (!amount) {
    return 0;
  }

  if (amount < 1000000) {
    return '$ ' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const magnitude = Math.floor(Math.log10(amount) / 3);
  const divisor = 1000 ** magnitude;
  const suffix = ['', 'K', 'M', 'B', 'T', 'Q', 'Qu'];

  const formattedAmount = (amount / divisor).toFixed(2);
  const suffixIndex = magnitude < suffix.length ? magnitude : suffix.length - 1;
  const formattedAmountWithSuffix = `${formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${
    suffix[suffixIndex]
  }`;

  return '$ ' + formattedAmountWithSuffix;
};

export const checkIfBusiness = (type) => {
  if (!type && typeof type !== 'string') return false;
  if (type?.toLowerCase() === BUSINESS) return true;
  return false;
};

export const checkIfIndividual = (type) => {
  if (!type && typeof type !== 'string') return false;
  if (type?.toLowerCase() === INDIVIDUAL) return true;
  return false;
};

/**
 * If the number of selected options is less than 15, then display the children. Otherwise, display a
 * message
 * @param {object} props
 * @returns Menu component
 */
export const Menu = (props) => {
  const optionSelectedLength = props.getValue().length || 0;
  return (
    <components.Menu {...props}>
      {optionSelectedLength < 15 ? (
        props.children
      ) : (
        <div className="w-100 text-center" style={{ margin: '15px 0' }}>
          {i18next.t('admin_max_limit_achieved')}
        </div>
      )}
    </components.Menu>
  );
};

export const getDateAndTime = (date) => {
  const getDate = moment(date)?.format('MMM DD');
  const getTime = moment(date)?.format('h:mm A');

  return `${getDate}, ${getTime}`;
};

// Sanitize text input to prevent XSS attacks
export const sanitizeTextInput = (value) =>
  value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '') // strip <script>...</script>
    .replace(/<[^>]*>/g, '') // strip any remaining HTML tags
    .replace(/[<>"'`]/g, '') // strip characters that can break out of an attribute/context
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');
